import { generatePath, Link } from 'react-router-dom';
import { BuildingAssigneeReadModel } from '@client/api';
import {
  EmptyPlaceholder,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  TableCell,
  toPercent,
} from '@client/toolkit';
import { useTranslation } from 'react-i18next';
import { CompaniesPaths } from '../../../companies/companies.router';
import { useTenants } from '../../../../hooks/useTenants/useTenants';
import { TenantSelectionEnum } from '../../../../redux';

const PLACEHOLDER = '\u2013';

export interface CompaniesTableProps {
  selectedCompanies: BuildingAssigneeReadModel[];
  isLoading: boolean;
  role: 'Owner' | 'Manager' | 'Investor';
}

export function CompaniesTable({ selectedCompanies, isLoading, role }: CompaniesTableProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { selectedTenant } = useTenants();
  const currentTenantId = selectedTenant && selectedTenant !== TenantSelectionEnum.All ? selectedTenant.id : null;

  // Owners that are the registered matrikkel hjemmelshaver carry an ownership share; show the
  // share column only when at least one assignee has one (never for investors/managers).
  const showShares = (selectedCompanies ?? []).some((c) => c.ownershipShare != null);
  const colSpan = showShares ? 3 : 2;

  return (
    <Table className="rounded-md overflow-hidden">
      <TableHead>
        <TableHeadRow>
          <TableHeadCell className="rounded-tl-md w-[80%]">{tGlobal('company.name')}</TableHeadCell>
          <TableHeadCell className={showShares ? '' : 'rounded-tr-md'}>{tGlobal('company.role')}</TableHeadCell>
          {showShares && <TableHeadCell className="rounded-tr-md">{tGlobal('company.share')}</TableHeadCell>}
        </TableHeadRow>
      </TableHead>

      <TableBody>
        {!isLoading && (!selectedCompanies || selectedCompanies.length === 0) && (
          <TableRow>
            <TableCell colSpan={colSpan} className="p-4">
              <EmptyPlaceholder fullWidth>
                {tCommon('noEntityFound', {
                  entity: tGlobal('entity.company', { context: 'plural' }).toLowerCase(),
                })}
              </EmptyPlaceholder>
            </TableCell>
          </TableRow>
        )}
        {selectedCompanies &&
          selectedCompanies.length > 0 &&
          selectedCompanies.map((company: BuildingAssigneeReadModel) => (
            <TableRow key={company.id}>
              <TableCell>
                <span className="inline-flex items-center gap-2">
                  {company.id === currentTenantId ? (
                    <Link
                      to="/profile"
                      className="hover:text-brand-primary transition-colors"
                    >
                      {company.name}
                    </Link>
                  ) : company.companyId ? (
                    <Link
                      to={generatePath(CompaniesPaths.Company, { id: company.companyId })}
                      className="hover:text-brand-primary transition-colors"
                    >
                      {company.name}
                    </Link>
                  ) : (
                    company.name
                  )}
                  {company.ownershipShare != null && (
                    <span className="text-xs px-2 py-0.5 rounded bg-brand-primary/15 text-brand-primary font-medium whitespace-nowrap">
                      {tGlobal('company.hjemmelshaver')}
                    </span>
                  )}
                </span>
              </TableCell>
              <TableCell>{tType(`RelationshipType.${role}`)}</TableCell>
              {showShares && (
                <TableCell>
                  {company.ownershipShare != null ? toPercent(company.ownershipShare) : PLACEHOLDER}
                </TableCell>
              )}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
}
